
import {Component, Prop, Vue, Watch} from 'vue-property-decorator';
import {SweetModal} from 'sweet-modal-vue';
// @ts-ignore
import Sortable from 'vue-drag-sortable';
import NevronInput from '@/components/NevronInput.vue';
import stores from '@/stores';
import {Dashboards} from '@/stores/Dashboard';
import NevronImageField from '@/components/NevronImageField.vue';
import WeatherLocationModule, { WeatherLocationModuleItem } from '@/stores/modules/WeatherLocationModule';
import { bus } from '@/helpers/functions';

@Component({
  components: {
    SweetModal,
    Sortable,
    NevronInput,
    NevronImageField,
  },
})
export default class EditPanel extends Vue {
  @Prop()
  panel: any | null;
  @Prop()
  panelIndex: any | null;
  @Prop()
  noImageIcon: any | null;
  localPanel = null;

  dragData: any = {};
  services: any = [];
  mobileDashboards: any = [];
  combinedServices: any = [];
  selectedServiceOrDashboard: string = '';
  externalUrl: string = '';
  module!: Dashboards;

  selectedWeather: WeatherLocationModuleItem | null = null;
  weathers: WeatherLocationModuleItem[] | [] = [];
  selectedWeatherModule: any = null;
  weatherModules: IModule[] | [] = [];

  hidePanelTypes: any = ['mood', 'weather', 'never-opened', 'banner'];

  @Watch('panel')
  loadProp() {
    if (this.panel) {
      this.localPanel = JSON.parse(JSON.stringify(this.panel));

      // Convert DASHBOARD to DIGITAL_SERVICE for UI (radio button binding)
      // but keep linkType to identify it as a dashboard
      // @ts-ignore
      if (this.localPanel.linkAction === 'DASHBOARD') {
        // @ts-ignore
        this.localPanel.linkAction = 'DIGITAL_SERVICE';
        // @ts-ignore
        if (!this.localPanel.linkType) {
          // @ts-ignore
          this.localPanel.linkType = 'dashboard';
        }
      }

      // Set external URL if it's an external link
      // @ts-ignore
      if (this.localPanel.linkAction === 'EXTERNAL_LINK') {
        // @ts-ignore
        this.externalUrl = this.localPanel.linkElementId || '';
      } else {
        this.externalUrl = '';
      }
      // Always ensure data is loaded when panel changes
      this.ensureDataLoaded();
    }
  }

  // Method to ensure data is loaded (can be called when modal opens)
  ensureDataLoaded() {
    // Always reload data when panel changes to ensure fresh data
    this.loadData().then(() => {
      this.$nextTick(() => {
        this.updateSelectedServiceOrDashboard();
      });
    });
  }

  @Watch('localPanel.linkAction')
  onLinkActionChange(newAction: string) {
    if (!this.localPanel) return;

    if (newAction === 'EXTERNAL_LINK') {
      // When switching to external link, clear service/dashboard selection
      this.selectedServiceOrDashboard = '';
      // @ts-ignore
      this.localPanel.linkModuleId = null;
      // @ts-ignore
      this.localPanel.linkType = null;
      // Set external URL from linkElementId if it exists
      // @ts-ignore
      this.externalUrl = this.localPanel.linkElementId || '';
    } else if (newAction === 'DIGITAL_SERVICE') {
      // When switching to digital service, clear external URL
      this.externalUrl = '';
    }
  }

  mounted() {
    this.module = new Dashboards({name: 'dashboard', routeName: 'name'});
    this.loadData();
  }

  async loadData() {
    try {
      // Initialize combinedServices as empty array if not already set
      if (!this.combinedServices) {
        this.combinedServices = [];
      }

      // Fetch both services and dashboards in parallel
      const [servicesResponse, dashboardsResponse] = await Promise.all([
        stores.modules.modules().catch((err) => {
          console.error('Error fetching services:', err);
          return [];
        }),
        stores.dashboards.fetchData(1, null, 100, 'Mobile').catch((err) => {
          console.error('Error fetching dashboards:', err);
          return { data: [] };
        })
      ]);

      // Services should be an array directly
      this.services = Array.isArray(servicesResponse) ? servicesResponse : [];

      // Dashboards come with a data property
      this.mobileDashboards = Array.isArray(dashboardsResponse?.data) ? dashboardsResponse.data : [];

      console.log('Services loaded:', this.services.length);
      console.log('Dashboards loaded:', this.mobileDashboards.length);

      // If both are empty, log a warning
      if (this.services.length === 0 && this.mobileDashboards.length === 0) {
        console.warn('No services or dashboards loaded');
      }

      this.updateCombinedServices();

      console.log('Combined services:', this.combinedServices.length);

      // Update selection after data is loaded
      this.$nextTick(() => {
        this.updateSelectedServiceOrDashboard();
      });
    } catch (error) {
      console.error('Error loading data:', error);
      this.services = [];
      this.mobileDashboards = [];
      this.updateCombinedServices();
    }
  }

  updateCombinedServices() {
    // Combine services and dashboards into a single array
    const servicesList = Array.isArray(this.services)
      ? this.services.map((service: any) => ({
          ...service,
          type: 'service'
        }))
      : [];

    const dashboardsList = Array.isArray(this.mobileDashboards)
      ? this.mobileDashboards.map((dashboard: any) => ({
          ...dashboard,
          type: 'dashboard'
        }))
      : [];

    // Use Vue.set or direct assignment to ensure reactivity
    this.$set(this, 'combinedServices', [...servicesList, ...dashboardsList]);
  }

  updateSelectedServiceOrDashboard() {
    if (!this.localPanel) return;

    const panel: any = this.localPanel;

    // Check if it's a dashboard link
    // Dashboard is identified by linkType === 'dashboard' OR
    // linkAction === 'DASHBOARD' OR
    // (linkElementId exists, no linkModuleId, and linkAction is DIGITAL_SERVICE)
    const isDashboard = panel.linkType === 'dashboard' ||
                       panel.linkAction === 'DASHBOARD' ||
                       (panel.linkElementId && !panel.linkModuleId &&
                        (panel.linkAction === 'DIGITAL_SERVICE' || panel.linkAction === 'DASHBOARD'));

    if (isDashboard && panel.linkElementId) {
      this.selectedServiceOrDashboard = 'dashboard_' + panel.linkElementId;
    } else if (panel.linkModuleId) {
      this.selectedServiceOrDashboard = 'service_' + panel.linkModuleId;
    } else {
      this.selectedServiceOrDashboard = '';
    }
  }

  handleServiceSelection(event: any) {
    if (!this.localPanel) return;

    const selectedValue = event.target.value;
    if (!selectedValue) {
      // @ts-ignore
      this.localPanel.linkType = null;
      // @ts-ignore
      this.localPanel.linkElementId = null;
      // @ts-ignore
      this.localPanel.linkModuleId = null;
      this.externalUrl = '';
      return;
    }

    // Clear external URL when selecting service/dashboard
    this.externalUrl = '';
    // Ensure linkAction is set to DIGITAL_SERVICE when selecting from dropdown
    // @ts-ignore
    this.localPanel.linkAction = 'DIGITAL_SERVICE';

    // Parse the selected value (format: 'service_123' or 'dashboard_456')
    const [type, id] = selectedValue.split('_');

    if (type === 'dashboard') {
      // For dashboard: use linkElementId, clear linkModuleId, but keep linkAction as DIGITAL_SERVICE
      // @ts-ignore
      this.localPanel.linkType = 'dashboard';
      // @ts-ignore
      this.localPanel.linkElementId = id;
      // @ts-ignore
      this.localPanel.linkModuleId = null;
    } else if (type === 'service') {
      // For service: use linkModuleId, clear linkElementId
      // @ts-ignore
      this.localPanel.linkType = null;
      // @ts-ignore
      this.localPanel.linkElementId = null;
      // @ts-ignore
      this.localPanel.linkModuleId = id;
    }
  }

  handleExternalUrlChange() {
    if (!this.localPanel) return;
    // When external URL changes, update linkElementId only if external link is selected
    // @ts-ignore
    if (this.localPanel.linkAction === 'EXTERNAL_LINK') {
      // @ts-ignore
      this.localPanel.linkElementId = this.externalUrl;
      // @ts-ignore
      this.localPanel.linkModuleId = null;
      // @ts-ignore
      this.localPanel.linkType = null;
    }
  }

  panelTitle (type:any) {
    if (type === 'mood') {
      return 'manage-my-mood-panel';
    } else if (type === 'weather') {
      return 'manage-weather-panel';
    } else if (type === 'never-opened') {
      return 'manage-never-opened-panel';
    } else if (type === 'banner') {
      return 'manage-banner-panel';
    } else {
      return 'manage-collection-panel';
    }
  }

  sort(e: any) {
    const {oldIndex, newIndex} = e;
    console.log(oldIndex, newIndex);
  }

  openTranslator(localText: any, localKey: any, referenceKey: any) {
    bus().$emit('openTranslator', localText, localKey, referenceKey);
  }

  imageSelected(image: IMediaItem) {
    if (this.localPanel) {
      // @ts-ignore
      this.localPanel.dashboardBackground = (image) ? image.id : null;
      // @ts-ignore
      this.localPanel.background = image;
    }
  }

  sorting(e: any) {
    const {oldIndex, newIndex} = e;
    this.rearrange(this.panel.elements, oldIndex, newIndex);
  }

  sortend(e: any, list: any) {
    const {oldIndex, newIndex} = e;
    this.rearrange(list, oldIndex, newIndex);
  }

  rearrange(array: any, oldIndex: any, newIndex: any) {
    if (oldIndex > newIndex) {
      array.splice(newIndex, 0, array[oldIndex]);
      array.splice(oldIndex + 1, 1);
    } else {
      array.splice(newIndex + 1, 0, array[oldIndex]);
      array.splice(oldIndex, 1);
    }
  }

  updateRatio(option: string) {
    // @ts-ignore
    this.localPanel.elementType = option;
  }

  isSelected(ration: string) {
    // @ts-ignore
    return this.localPanel.elementType === ration;
  }

  cancel() {

    if (localStorage.getItem('createPanel') === 'true') {
      this.$emit('close');
    }
    // @ts-ignore
    this.$children[0].close();

    // Ensure body scroll is restored when modal closes
    this.$nextTick(() => {
      document.body.style.overflow = '';
    });

    if(this.localPanel === null) return
    // @ts-ignore
    if(this.localPanel.panelType === 'weather')
      this.onWeatherPanelCancel()
  }

  next() {

    if(this.localPanel === null) return

    // @ts-ignore
    if(this.localPanel.panelType === 'weather')
      this.onWeatherPanelSubmit()

    // Create a copy of localPanel for output
    const panelCopy = JSON.parse(JSON.stringify(this.localPanel));

    // Convert dashboard links back to DASHBOARD action before saving
    // (for consistency with how addBannerWidget creates new panels)
    if (panelCopy.linkType === 'dashboard' && panelCopy.linkElementId) {
      panelCopy.linkAction = 'DASHBOARD';
    }

    const output = {
      panel: panelCopy,
      panelIndex: this.panelIndex,
    };
    this.$emit('next', output);
    // @ts-ignore
    this.$children[0].close();

    // Ensure body scroll is restored when modal closes
    this.$nextTick(() => {
      document.body.style.overflow = '';
    });
  }

  @Watch('localPanel')
  onLocalPanelOpen() {
    if(this.panel === null) return
    if(this.panel.panelType === 'weather')
      return this.onWeatherPanelOpen()
  }

  async onWeatherPanelOpen()
  {
      this.weatherModules = await stores.modules.getSpecificModule('WEATHER');
      this.weatherModules.forEach((weatherModule) => {
          // @ts-ignore
          if(weatherModule.id == this.localPanel.linkModuleId)
            this.selectedWeatherModule = weatherModule
      })

      let weatherLocationModule = new WeatherLocationModule(this.selectedWeatherModule);
      let weatherLocations = await weatherLocationModule.getAllWeatherLocations();
      this.weathers = weatherLocations.data

      this.weathers.forEach((weatherLocation) => {
          // @ts-ignore
          if(weatherLocation.id == this.localPanel.linkElementId)
            this.selectedWeather = weatherLocation
      })
  }

  onWeatherPanelSubmit()
  {
    // @ts-ignore
    this.localPanel.linkElementId = this.selectedWeather.id
    // @ts-ignore
    this.localPanel.linkModuleId = this.selectedWeatherModule.id
  }


  async onWeatherPanelCancel()
  {
      this.weatherModules.forEach((weatherModule) => {
          // @ts-ignore
          if(weatherModule.id == this.localPanel.linkModuleId)
            this.selectedWeatherModule = weatherModule
      })

      let weatherLocationModule = new WeatherLocationModule(this.selectedWeatherModule);
      let weatherLocations = await weatherLocationModule.getAllWeatherLocations();
      this.weathers = weatherLocations.data

      this.weathers.forEach((weatherLocation) => {
          // @ts-ignore
          if(weatherLocation.id == this.localPanel.linkElementId)
            this.selectedWeather = weatherLocation
      })
  }

  async loadWeatherLocations()
  {
      let weatherLocationModule = new WeatherLocationModule(this.selectedWeatherModule);
      let weatherLocations = await weatherLocationModule.getAllWeatherLocations();
      this.weathers = weatherLocations.data
  }

}
